import cn from '@integrations/classnames'
import type { ComponentProps, ReactNode } from '@integrations/react'
import { useLayoutEffect, useRef } from '@integrations/react'
import { MMTabs } from 'mmfintech-portal-commons'
import styles from './SlidingTabs.module.scss'

interface SlidingTab {
  value: string
  label: ReactNode
  hidden?: boolean
  disabled?: boolean
}

interface Props {
  tabs: SlidingTab[]
  activeTab: string
  setActiveTab: (value: string) => void
  /**
   * For a bar that is rendered again by each page it belongs to: the pill's last place is remembered under this key,
   * so the new bar starts the pill there and slides it to the active tab.
   */
  memoryKey?: string
  className?: string
}

interface ThumbBox {
  left: number
  top: number
  width: number
  height: number
}

const lastThumbBoxes = new Map<string, ThumbBox>()

function setThumbBox(thumb: HTMLElement, box: ThumbBox) {
  thumb.style.transform = `translate(${box.left}px, ${box.top}px)`
  thumb.style.width = `${box.width}px`
  thumb.style.height = `${box.height}px`
}

/** The tab bar (`MMTabs`) with a pill that slides under the active tab instead of jumping to it. */
function SlidingTabs({ tabs, activeTab, setActiveTab, memoryKey, className }: Props) {
  const barRef = useRef<HTMLDivElement>(null)
  const thumbRef = useRef<HTMLSpanElement>(null)

  const shownTabs = tabs
    .filter((tab) => !tab.hidden)
    .map((tab) => tab.value)
    .join('|')

  // the pill sits under the active tab and slides to it when the tab changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: the pill follows the active tab and the tabs that are shown
  useLayoutEffect(() => {
    const bar = barRef.current
    const thumb = thumbRef.current
    const tab = bar?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
    if (!bar || !thumb || !tab) return

    function measure(): ThumbBox {
      const barBox = bar.getBoundingClientRect()
      const tabBox = tab.getBoundingClientRect()
      return {
        left: tabBox.left - barBox.left,
        top: tabBox.top - barBox.top,
        width: tabBox.width,
        height: tabBox.height
      }
    }

    function place(animated: boolean) {
      const box = measure()
      thumb.style.transition = animated ? '' : 'none'
      setThumbBox(thumb, box)
      if (memoryKey) lastThumbBoxes.set(memoryKey, box)
    }

    const appeared = thumb.dataset.ready === 'true'
    const remembered = memoryKey ? lastThumbBoxes.get(memoryKey) : undefined
    if (!appeared && remembered) {
      // a new bar starts the pill where it was before, then slides
      thumb.style.transition = 'none'
      setThumbBox(thumb, remembered)
      void thumb.offsetWidth
      place(true)
    } else {
      place(appeared)
    }
    thumb.dataset.ready = 'true'

    if (typeof ResizeObserver === 'undefined') return
    // an observer reports once when it starts; only later size changes move the pill without sliding
    let watching = false
    const observer = new ResizeObserver(() => {
      if (watching) place(false)
      watching = true
    })
    observer.observe(bar)
    return () => observer.disconnect()
  }, [activeTab, shownTabs, memoryKey])

  return (
    <div ref={barRef} className={cn(styles['sliding-tabs'], 'sliding-tabs', className)}>
      {/* MMTabs renders labels as React children, but its published types only allow strings. */}
      <MMTabs
        tabs={tabs as unknown as ComponentProps<typeof MMTabs>['tabs']}
        activeTab={activeTab}
        setActiveTab={setActiveTab as ComponentProps<typeof MMTabs>['setActiveTab']}
      />
      <span ref={thumbRef} className={cn(styles['sliding-tabs__thumb'])} aria-hidden='true' />
    </div>
  )
}

export type { SlidingTab }
export default SlidingTabs
